import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { parseEventAbiFromPayload } from "../../eventAbiUtils";
import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

interface DecodedLog {
  triggeredAt: string;
  params: Record<string, unknown>;
  raw: {
    address: string;
    topics: string[];
    data: string;
    blockNumber: number;
    transactionHash: string;
    logIndex: number;
    sender?: string;
  };
  decodeError?: string;
}

interface GetLogsResult {
  logs: DecodedLog[];
  latestBlock: number;
  searchedBlocks: number;
  partialResults?: boolean;
}

/**
 * Fetch recent contract events via the `evm-get-logs` action proxy.
 *
 * Goes through b3os-workflow → b3os-actions so the request uses Doppler-configured
 * RPCs (not public nodes), respects server-side retries / batching, and keeps RPC
 * credentials out of the browser. The server action does the progressive-range
 * search and decoding; we just pass the trigger config through.
 */
export const evmLogFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const { triggerPayload, signal } = ctx;

  const chainId = triggerPayload.chainId as number | undefined;
  const contractAddress = triggerPayload.contractAddress as string | undefined;
  const { eventAbi } = parseEventAbiFromPayload(triggerPayload.eventAbi);

  if (!chainId || !eventAbi?.name || !eventAbi?.inputs) return [];

  const payload: Record<string, unknown> = {
    chainId,
    eventAbi,
    limit: 5,
  };
  if (contractAddress) payload.contractAddress = contractAddress;

  const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/action-proxy/evm-get-logs/query`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ payload }),
    signal,
  });

  if (!response.ok) {
    let reason = `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as { error?: { message?: string }; message?: string };
      reason = body.error?.message ?? body.message ?? reason;
    } catch {
      // ignore json parse error — keep status-code reason
    }
    throw new Error(`Failed to fetch ${eventAbi.name} logs on chain ${chainId}: ${reason}`);
  }

  const data = (await response.json()) as {
    code: number;
    message?: string;
    data?: {
      success?: boolean;
      // On success, result matches GetLogsResult. On action failure, result
      // contains `{ error: { code, message } }` (see b3os-workflow action
      // proxy service — it forwards structured action errors this way).
      result?: GetLogsResult & { error?: { code?: string; message?: string } };
    };
  };
  // b3os-workflow wraps every response in `{ code, message, data }`.
  // `code === 0` is the success sentinel; non-zero means the gateway or
  // action-proxy layer itself failed. Inside `data.success` we further
  // distinguish structured action failures (which carry `result.error`).
  if (data.code !== 0 || !data.data?.success) {
    const msg = data.data?.result?.error?.message ?? data.message ?? `code=${data.code}`;
    throw new Error(`Failed to fetch ${eventAbi.name} logs on chain ${chainId}: ${msg}`);
  }

  return (data.data.result?.logs ?? []) as unknown as Record<string, unknown>[];
};
